import { useEffect, useState } from "react";

import { toCount } from "common/utils/formatting";

import Modal from "client/common/components/deprecated-modals/Modal";
import ModalLayout from "client/common/components/deprecated-modals/ModalLayout";

import { FilterDefinition, FilterValue } from "../types";
import { clearedFilterValues } from "../utils";
import FilterPanel from "./FilterPanel";

type FilterPanelModalProps<TSearch, TFilterOptions> = {
  definitions: FilterDefinition<TSearch, FilterValue, TFilterOptions>[];
  options: TFilterOptions;
  isVisible: boolean;
  onClose: () => void;
  value: TSearch;
  onChange: (search: TSearch) => void;
};

/** The filter panel in a modal, for viewports too narrow to show it beside the grid. */
const FilterPanelModal = <TSearch, TFilterOptions>({
  definitions,
  options,
  isVisible,
  onClose,
  value,
  onChange,
}: FilterPanelModalProps<TSearch, TFilterOptions>) => {
  const [updatedFilters, setUpdatedFilters] = useState(value);

  useEffect(() => {
    if (isVisible) {
      setUpdatedFilters(value);
    }
    // The params have to be current when the modal first comes into view
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const resetSearch = () =>
    setUpdatedFilters({
      ...updatedFilters,
      ...clearedFilterValues(definitions),
    });

  const applyFilters = () => {
    onClose();
    onChange({ ...updatedFilters });
  };

  // Every filter counts, including any whose options came back empty: Reset is the only way to
  // clear a value that has no control on screen.
  const selectedCount = definitions.reduce(
    (total, definition) => total + definition.selectedFrom(updatedFilters).length,
    0,
  );

  return (
    <Modal visible={isVisible} onClose={onClose}>
      <ModalLayout>
        <ModalLayout.Header title="Apply Filters" onClose={onClose} />
        <ModalLayout.Content>
          <FilterPanel<TSearch, TFilterOptions>
            definitions={definitions}
            filterOptions={options}
            filters={updatedFilters}
            onChange={(next) => setUpdatedFilters({ ...updatedFilters, ...next })}
            hasSelectedFilters={!!selectedCount}
            onClearAll={resetSearch}
            showHeader={false}
          />
        </ModalLayout.Content>
        <ModalLayout.Actions>
          <ModalLayout.Actions.Button
            variant="secondary"
            onClick={resetSearch}
            disabled={!selectedCount}
          >
            Reset {toCount(selectedCount, "Filter")}
          </ModalLayout.Actions.Button>
          <ModalLayout.Actions.Button onClick={applyFilters}>Apply</ModalLayout.Actions.Button>
        </ModalLayout.Actions>
      </ModalLayout>
    </Modal>
  );
};

export default FilterPanelModal;
